import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, Radio, FileText, Megaphone, Eye, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const contentTypeIcons = {
  video: Play,
  podcast: Radio,
  article: FileText,
  announcement: Megaphone,
};

const contentTypeColors = {
  video: "#EF4444",
  podcast: "#8B5CF6",
  article: "#10B981",
  announcement: "#F59E0B",
};

export default function LiveContentPage() {
  const [content, setContent] = useState([]);
  const [filter, setFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        let url = `${API}/content`;
        if (filter !== "all") {
          url += `?content_type=${filter}`;
        }
        const res = await axios.get(url);
        setContent(res.data);
      } catch (error) {
        console.error("Error fetching content:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, [filter]);

  const filters = [
    { key: "all", label: "All Content" },
    { key: "video", label: "Videos" },
    { key: "podcast", label: "Podcasts" },
    { key: "article", label: "Articles" },
  ];

  const liveContent = content.filter((c) => c.is_live);
  const regularContent = content.filter((c) => !c.is_live);

  return (
    <div className="page-container min-h-screen" data-testid="live-content-page">
      {/* Hero */}
      <section className="py-16 md:py-24 hero-pattern relative overflow-hidden">
        <div className="hero-glow top-1/4 right-1/4 opacity-40" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 mb-6">
              <span className="live-indicator" />
              <span className="text-sm text-red-400">Live Content Hub</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
              AI Horse-Powered <span className="gradient-text">Content Hub</span>
            </h1>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Watch live streams, listen to podcasts, read articles, and stay updated
              with the latest from the Best of Best community.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-10">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                filter === f.key
                  ? "bg-[#7C3AED] text-white"
                  : "bg-white/5 text-[#94A3B8] hover:text-white hover:bg-white/10"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Live Now Section */}
        {liveContent.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <span className="live-indicator" />
              <h2 className="text-2xl font-bold font-['Outfit'] text-white">Live Now</h2>
            </div>
            
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid md:grid-cols-2 gap-6"
            >
              {liveContent.map((item) => {
                const Icon = contentTypeIcons[item.content_type] || Play;
                const color = contentTypeColors[item.content_type] || "#7C3AED";
                
                return (
                  <motion.div
                    key={item.id}
                    variants={itemVariants}
                    className="glass-card rounded-2xl overflow-hidden group cursor-pointer card-lift"
                    data-testid={`live-content-${item.id}`}
                  >
                    <div className="relative aspect-video">
                      <img
                        src={item.thumbnail_url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="content-overlay absolute inset-0" />
                      
                      {/* Live Badge */}
                      <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1 rounded-full bg-red-500 text-white text-sm font-medium">
                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                        LIVE
                      </div>
                      
                      {/* Play Button */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      
                      {/* View Count */}
                      <div className="absolute bottom-4 right-4 flex items-center gap-2 text-white text-sm">
                        <Eye className="w-4 h-4" />
                        {item.view_count} watching
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-2 line-clamp-1">
                        {item.title}
                      </h3>
                      <p className="text-[#94A3B8] text-sm line-clamp-2">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </section>
        )}

        {/* Regular Content */}
        <section>
          <h2 className="text-2xl font-bold font-['Outfit'] text-white mb-6">
            {filter === "all" ? "All Content" : `${filters.find(f => f.key === filter)?.label || "Content"}`}
          </h2>
          
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="glass-card rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-video bg-white/10" />
                  <div className="p-4">
                    <div className="h-5 bg-white/10 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-white/10 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : regularContent.length === 0 ? (
            <div className="text-center py-20 glass-card rounded-2xl">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Play className="w-8 h-8 text-[#94A3B8]" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">No content yet</h3>
              <p className="text-[#94A3B8]">Check back soon for new content!</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {regularContent.map((item) => {
                const Icon = contentTypeIcons[item.content_type] || Play;
                const color = contentTypeColors[item.content_type] || "#7C3AED";
                
                return (
                  <motion.div
                    key={item.id}
                    variants={itemVariants}
                    className="glass-card rounded-2xl overflow-hidden group cursor-pointer card-lift"
                    data-testid={`content-${item.id}`}
                  >
                    <div className="relative aspect-video">
                      <img
                        src={item.thumbnail_url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="content-overlay absolute inset-0" />
                      
                      {/* Type Badge */}
                      <div className="absolute top-4 left-4">
                        <Badge
                          className="text-white text-xs"
                          style={{ backgroundColor: color }}
                        >
                          <Icon className="w-3 h-3 mr-1" />
                          {item.content_type}
                        </Badge>
                      </div>
                      
                      {/* Play/Action Button */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <h3 className="text-white font-semibold font-['Outfit'] mb-2 line-clamp-1">
                        {item.title}
                      </h3>
                      <p className="text-[#94A3B8] text-sm line-clamp-2 mb-3">
                        {item.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-[#64748B]">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {item.view_count} views
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </section>
      </div>
    </div>
  );
}
